
import React, { useState } from "react";
import { Modal } from "./Modal";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import type { AttachmentMeta } from "../types/attachments";
import { formatExcelOrTimestamp } from "../utils/dateUtils";
import { getContractTypeLabel } from "../constants/contractTypes";

type Props = {
  data: any;
  onClose: () => void;
  title?: string;
};

// Generic Field Map (Fallback)
const FIELD_MAP: Record<string, string> = {
  id: "系統編號",
  title: "標題",
  notes: "備註",
  attachments: "附件",
  history: "歷程紀錄",
  status: "狀態",
};

// 1. Brand Assets Config
const BRAND_FIELD_CONFIG: {
  key: string;
  label: string;
  formatter?: (v: any, row?: any) => string;
}[] = [
  { key: "status", label: "狀態" },
  { key: "name", label: "品牌名稱" },
  { key: "region", label: "地區" },
  { key: "type", label: "類型" },
  { key: "owner", label: "負責單位" },
  { key: "group", label: "品牌群組" },
  { key: "category", label: "商品類別" },
  { key: "expiryDate", label: "到期日", formatter: (v) => formatExcelOrTimestamp(v) },
  { key: "createdAt", label: "建立時間", formatter: (v) => formatExcelOrTimestamp(v) },
  { key: "updatedAt", label: "更新時間", formatter: (v) => formatExcelOrTimestamp(v) },
  { key: "lastUpdatedBy", label: "最後更新人員" },
  { key: "notes", label: "備註" },
];

// 2. Contracts Config (Localized Labels)
const CONTRACT_FIELD_CONFIG: {
  key: string;
  label: string;
  formatter?: (v: any, row?: any) => string;
}[] = [
  { key: "contractNo", label: "合約編號" },
  { key: "title", label: "標題" },
  { key: "contractType", label: "合約類型", formatter: (v) => getContractTypeLabel(v) },
  { key: "department", label: "部門", formatter: (v, row) => v || row?.ourDept || "" },
  { key: "counterparty", label: "相對人（對造）" },
  { key: "totalAmount", label: "合約金額", formatter: (v, row) => v ? `${row.currency || 'TWD'} ${Number(v).toLocaleString()}` : "" },
  { 
    key: "riskLevel", 
    label: "風險等級",
    formatter: (v) => v === 'high' ? '高' : v === 'medium' ? '中' : v === 'low' ? '低' : v
  },
  { key: "signDate", label: "簽約日", formatter: (v) => formatExcelOrTimestamp(v) },
  { key: "periodStart", label: "合約期間起日", formatter: (v) => formatExcelOrTimestamp(v) },
  { key: "periodEnd", label: "合約期間迄日", formatter: (v) => v ? formatExcelOrTimestamp(v) : "永久" },
  { key: "status", label: "狀態" },
  { key: "relatedContractNos", label: "關聯合約", formatter: (v) => Array.isArray(v) ? v.join(", ") : v },
  { key: "tags", label: "標籤", formatter: (v) => Array.isArray(v) ? v.join(", ") : v },
  { key: "reviewCode", label: "來源合約會審單號" },
  { key: "sealRequestCode", label: "對應用印申請單號" },
  { key: "createdAt", label: "建立時間", formatter: (v) => formatExcelOrTimestamp(v) },
  { key: "updatedAt", label: "更新時間", formatter: (v) => formatExcelOrTimestamp(v) },
  { key: "lastUpdatedBy", label: "最後更新者" },
  { key: "notes", label: "備註" },
];

export function PreviewModal({ data, onClose, title = "資料預覽" }: Props) {
  const [previewAttachment, setPreviewAttachment] = useState<AttachmentMeta | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!data) return null;

  // Detect Record Type
  const isBrandAsset = !!data.assetNo || (data.group && data.name && !data.contractNo);
  const isContract = !!data.contractNo || (data.periodStart !== undefined);

  const handleOpenAttachmentPreview = (att: AttachmentMeta) => {
    setPreviewAttachment(att);
    setPreviewOpen(true);
  };

  const renderAttachments = (files: any) => {
      const list = Array.isArray(files) ? files as AttachmentMeta[] : [];
      if (list.length === 0) return "-";
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {list.map((f, idx) => (
            <button 
              key={idx}
              type="button"
              className="lop-link-button"
              onClick={() => handleOpenAttachmentPreview(f)}
              title={f.name}
            >
              📄 {f.name}
            </button>
          ))}
        </div>
      );
  };

  const renderConfigFields = (config: typeof BRAND_FIELD_CONFIG) => {
      return config.map(field => {
          const raw = data[field.key];
          // Skip empty if it's not a boolean false or number 0 or if explicitly null
          if (raw === undefined || raw === "" || (raw === null && field.key !== 'periodEnd')) return null;
          
          const text = field.formatter ? field.formatter(raw, data) : String(raw);
          return (
              <div key={field.key} style={{ borderBottom: "1px solid #333", paddingBottom: 8 }}>
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>
                  {field.label}
                </div>
                <div style={{ color: "var(--text)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                  {text}
                </div>
              </div>
          );
      });
  };

  const renderContent = () => {
      // 1. Contract View
      if (isContract) {
          return renderConfigFields(CONTRACT_FIELD_CONFIG);
      }

      // 2. Brand Asset View
      if (isBrandAsset) {
          return renderConfigFields(BRAND_FIELD_CONFIG);
      }

      // 3. Generic View (Fallback)
      return Object.entries(data).map(([key, value]) => {
        if (key === "id" || key === "logoBlobId") return null; 
        
        // Handle Attachments specifically
        if (key === "attachments" || key === "附件") {
             return (
                <div key={key} style={{ borderBottom: "1px solid #333", paddingBottom: 8 }}>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>附件</div>
                    <div>{renderAttachments(value)}</div>
                </div>
             );
        }

        const label = FIELD_MAP[key] || key;
        
        let displayValue: any = value;
        if (Array.isArray(value)) displayValue = value.length > 0 ? "[Array]" : "-";
        if (typeof value === "boolean") displayValue = value ? "是" : "否";
        if (typeof value === "object" && (value as any)?.seconds) displayValue = formatExcelOrTimestamp(value);

        return (
          <div key={key} style={{ borderBottom: "1px solid #333", paddingBottom: 8 }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>
              {label}
            </div>
            <div style={{ color: "var(--text)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
              {String(displayValue || "-")}
            </div>
          </div>
        );
      });
  };

  return (
    <>
      <Modal title={title} onClose={onClose} width={600}>
        <div style={{ display: "grid", gap: 12 }}>
          {renderContent()}
          
          {/* Always append Attachments at bottom if exist and not already rendered by generic loop */}
          {(isBrandAsset || isContract) && data.attachments && data.attachments.length > 0 && (
              <div style={{ borderBottom: "1px solid #333", paddingBottom: 8 }}>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>附件</div>
                  <div>{renderAttachments(data.attachments)}</div>
              </div>
          )}
        </div>
      </Modal>

      <AttachmentPreviewModal 
        open={previewOpen}
        attachment={previewAttachment} 
        onClose={() => {
            setPreviewOpen(false);
            setPreviewAttachment(null);
        }} 
      />
    </>
  );
}
